import React, { useState } from "react";
import { useReviewSchedule, CADENCES } from "../../hooks/useReviewSchedule";
import { useSort } from "../../hooks/useSort";
import { Alert, EmptyState, SortHeader } from "../../components";
import { fmtDate } from "../../lib/dates";
import { fmtScore } from "../../lib/ratings";
import "./Reviews.css";

// Compared as dates, not as the strings they arrive as.
const SCHEDULE_DATE_KEYS = ["next_review", "last_review", "last_scan"];

const VIEWS = [
  { value: "upcoming", label: "Next 90 days", countKey: "upcoming" },
  { value: "overdue", label: "Overdue", countKey: "overdue" },
  { value: "unscheduled", label: "Not scheduled", countKey: "unscheduled" },
  { value: "all", label: "All", countKey: "all" },
];

// Who is due. Overdue first, with the last scan alongside so the two cadences line up.
const ReviewScheduleScreen = ({ reviewOrgId, reviewOrgName, onClearReviewOrg }) => {
  const { schedule, counts, today, loading, error, setCadence } = useReviewSchedule(reviewOrgId);
  const [view, setView] = useState("upcoming");

  const horizon = (() => {
    if (!today) return "";
    const d = new Date(today); d.setMonth(d.getMonth() + 3);
    return d.toISOString().slice(0, 10);
  })();

  const rows = schedule.filter((s) => {
    if (view === "all") return true;
    if (view === "overdue") return s.overdue;
    if (view === "unscheduled") return !s.next_review;
    return !s.overdue && s.next_review && s.next_review <= horizon;
  });

  // Sorted on next_review, not on the rendered "Due" text: that column shows
  // "Overdue 12 days" for some rows and a date for others, so ordering the words would
  // group every overdue client together alphabetically under "Overdue".
  const { sort, toggleSort, sorted } = useSort(rows, "next_review", SCHEDULE_DATE_KEYS, "asc");

  const dueLabel = (s) => {
    if (!s.next_review) return <span className="v2-dim">Not scheduled</span>;
    if (!s.overdue) return fmtDate(s.next_review);
    const days = Math.round((new Date(today) - new Date(s.next_review)) / 86400000);
    return (
      <span style={{ color: "var(--v2-danger)", fontWeight: 600 }}>
        Overdue {days} {days === 1 ? "day" : "days"}
      </span>
    );
  };

  return (
    <div className="v2-page">
      <div className="v2-pagehead">
        <div>
          <h2>Review schedule</h2>
          <p>Who is due. Overdue first, with the last scan alongside so the two cadences line up.</p>
        </div>
      </div>

      <div className="v2-views">
        {VIEWS.map((v) => (
          <button key={v.value} type="button"
                  className={`v2-view${view === v.value ? " on" : ""}`}
                  aria-current={view === v.value ? "page" : undefined}
                  onClick={() => setView(v.value)}>
            {v.label}
            {counts[v.countKey] != null && <span className="n">{counts[v.countKey]}</span>}
          </button>
        ))}
      </div>

      <Alert>{error}</Alert>

      {loading ? <p className="v2-hint">Loading…</p>
        : rows.length === 0 ? (
          reviewOrgId ? (
            <EmptyState title={`Nothing here for ${reviewOrgName || "this organization"}`}
              action={<button type="button" className="v2-btn" style={{ marginTop: ".75rem" }}
                              onClick={onClearReviewOrg}>Show all organizations</button>}>
              Business reviews is filtered to {reviewOrgName || "one organization"}, and it
              does not fall into this view.
            </EmptyState>
          ) : (
            <EmptyState title="Nothing here">
              {view === "overdue"
                ? "No review is overdue."
                : "No organization falls into this view."}
            </EmptyState>
          )
        ) : (
          <div className="v2-tablewrap" style={{ marginTop: ".875rem" }}>
            <table className="v2-table">
              <thead>
                <tr>
                  <SortHeader label="Organization" colKey="organization_name" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Cadence" colKey="cadence" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Due" colKey="next_review" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Last review" colKey="last_review" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Last scan" colKey="last_scan" sort={sort} onSort={toggleSort} />
                  <SortHeader label="Score" colKey="score" sort={sort} onSort={toggleSort} className="v2-num" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((s) => (
                  <tr key={s.organization_id}>
                    <td>{s.organization_name}</td>
                    <td>
                      {/* Cadence is editable inline: it is one choice per client, and a
                          dialog for a single select would be ceremony. */}
                      <select
                        className="v2-input v2-cadence"
                        value={s.cadence || ""}
                        aria-label={`Review cadence for ${s.organization_name}`}
                        onChange={(e) => setCadence(s.organization_id, e.target.value)}
                      >
                        <option value="">Not set</option>
                        {CADENCES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </td>
                    <td>{dueLabel(s)}</td>
                    <td className="v2-dim">{s.last_review ? fmtDate(s.last_review) : "None"}</td>
                    <td className="v2-dim">{s.last_scan ? fmtDate(s.last_scan) : "None"}</td>
                    <td className="v2-num">{fmtScore(s.score)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {/* <Alert tone="warning">
        <div>
          <b>Cadence is a manual choice until organizations carry revenue</b>
          The QBR demo derived it automatically from monthly recurring revenue. Scanner
          organizations store name, website, description and contacts only, so each
          client's frequency is set by hand for now.
        </div>
      </Alert> */}
    </div>
  );
};

export default ReviewScheduleScreen;
